// ABOUTME: Chat message component for investigation conversations.
// ABOUTME: Renders user and assistant messages with SDK content blocks.

import { memo } from 'react';
import { motion } from 'framer-motion';
import { User } from 'lucide-react';
import type { ContentBlock, ToolResultBlock, TextBlock, SmartAction } from '@/types/sdk';
import { AgentExecutionView } from './AgentExecutionView';
import { tryParseAsStructuredResponse } from './ContentBlockRenderer';

interface ChatMessageProps {
  role: 'user' | 'assistant';
  content: string | ContentBlock[];
  toolResults?: Map<string, ToolResultBlock>;
  messageId: string;
  onPin?: (messageId: string) => void;
  onUnpin?: (messageId: string) => void;
  isPinned?: boolean;
  showPinButton?: boolean;
  isStreaming?: boolean;
  currentPhase?: string;
  showSmartActions?: boolean;
  onSmartAction?: (action: SmartAction) => void;
}

// Memoized to prevent re-renders when parent state changes (e.g., input typing)
export const ChatMessage = memo(function ChatMessage({
  role,
  content,
  toolResults,
  messageId,
  onPin,
  onUnpin,
  isPinned = false,
  showPinButton = false,
  isStreaming = false,
  currentPhase,
  showSmartActions = false,
  onSmartAction,
}: ChatMessageProps) {
  if (role === 'user') {
    return <UserMessage content={content as string} />;
  }

  // Wrap callbacks to include messageId
  const handlePin = onPin ? () => onPin(messageId) : undefined;
  const handleUnpin = onUnpin ? () => onUnpin(messageId) : undefined;

  return (
    <AssistantMessage
      content={content as ContentBlock[]}
      toolResults={toolResults}
      onPin={handlePin}
      onUnpin={handleUnpin}
      isPinned={isPinned}
      showPinButton={showPinButton}
      isStreaming={isStreaming}
      currentPhase={currentPhase}
      showSmartActions={showSmartActions}
      onSmartAction={onSmartAction}
    />
  );
})

// User message bubble
function UserMessage({ content }: { content: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-start justify-end gap-3"
    >
      <div className="px-5 py-3 rounded-sm bg-primary-500 shadow-md max-w-2xl">
        <p className="text-base text-white font-medium leading-relaxed">{content}</p>
      </div>
      <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-primary-500 to-primary-400 p-[1px] shrink-0">
        <div className="w-full h-full rounded-full bg-surface-0 flex items-center justify-center">
          <User className="w-5 h-5 text-primary-600" />
        </div>
      </div>
    </motion.div>
  );
}

// Assistant message with content blocks
function AssistantMessage({
  content,
  toolResults,
  onPin,
  onUnpin,
  isPinned,
  showPinButton,
  isStreaming,
  currentPhase,
  showSmartActions,
  onSmartAction,
}: {
  content: ContentBlock[];
  toolResults?: Map<string, ToolResultBlock>;
  onPin?: () => void;
  onUnpin?: () => void;
  isPinned?: boolean;
  showPinButton?: boolean;
  isStreaming?: boolean;
  showSmartActions?: boolean;
  currentPhase?: string;
  onSmartAction?: (action: SmartAction) => void;
}) {
  // Ensure content is an array - may receive string during error states
  const contentArray = Array.isArray(content) ? content : [];

  // Check for pinnable content - tool_use blocks OR actual insight JSON (not text responses)
  const hasVisualContent = contentArray.some(block => {
    if (block.type === 'tool_use') return true;
    if (block.type === 'text') {
      const textBlock = block as TextBlock;
      const parsed = tryParseAsStructuredResponse(textBlock.text);
      // Only insights are pinnable, not text responses
      return parsed !== null && parsed.type === 'insight';
    }
    return false;
  });

  // Check if we have any displayable content (rationales, structured content, or JSON insights)
  const hasRationale = contentArray.some(
    block => block.type === 'tool_use' && (block as { input: { rationale?: string } }).input?.rationale
  );

  // Check for text blocks that contain JSON insights (these should be rendered)
  const hasJsonInsightText = contentArray.some(block => {
    if (block.type === 'text') {
      const textBlock = block as TextBlock;
      const parsed = tryParseAsStructuredResponse(textBlock.text);
      return parsed !== null; // Has parseable JSON insight
    }
    return false;
  });

  // Check for plain text (non-JSON) - shown as fallback when no structured content
  const hasPlainText = contentArray.some(block => {
    if (block.type === 'text') {
      const textBlock = block as TextBlock;
      const text = textBlock.text;
      if (!text || text.trim().length === 0) return false;
      // Only count as plain text if it's NOT a JSON insight
      const parsed = tryParseAsStructuredResponse(text);
      return parsed === null;
    }
    return false;
  });

  // Check for properly typed structured response blocks
  const hasTypedStructuredContent = contentArray.some(
    block => block.type === 'structured_response' || block.type === 'parse_error'
  );

  // Combined: has structured content if JSON insights or typed structured blocks exist
  const hasStructuredContent = hasJsonInsightText || hasTypedStructuredContent;

  // During streaming: always show (AgentExecutionView handles loading state)
  // After complete: show if we have structured content OR plain text as fallback
  const hasAnyContent = hasRationale || hasStructuredContent || hasPlainText;
  const shouldRender = isStreaming || hasAnyContent;

  if (!shouldRender) {
    return null;
  }

  return (
    <div className="flex items-start gap-3 w-full">

      {/* Avatar */}
      <div className="w-10 h-10 rounded-full bg-surface-100 flex items-center justify-center shrink-0 shadow-sm">
        <div className="w-5 h-5 bg-gradient-to-r from-primary-500 to-primary-400 rounded-full" />
      </div>

      {/* Message content */}
      <div
        className="flex-1 min-w-0 max-w-4xl"
        data-tour={isStreaming ? 'streaming-message' : undefined}
      >
        <AgentExecutionView
          content={contentArray}
          toolResults={toolResults || new Map()}
          isComplete={!isStreaming}
          currentPhase={currentPhase}
          onPin={onPin}
          onUnpin={onUnpin}
          isPinned={isPinned}
          showPinButton={showPinButton && hasVisualContent}
          showSmartActions={showSmartActions}
          onSmartAction={onSmartAction}
        />
      </div>
    </div>
  );
}

